import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { LazyQueryTrigger, UseLazyQuery, UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import React, { useCallback, useContext, useDeferredValue, useEffect, useMemo, useState } from 'react';
import InfoTooltip from 'components/InfoTooltip';
import { createPortal } from 'react-dom';
import { FormattedMessage, FormattedNumber, FormattedPlural } from 'react-intl';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';

import { buttonStyle, ICON_SLOT } from 'components/buttons/buttonStyle';
import { FileDownloadOutlined, FilterList } from 'components/icons';
import { Button } from 'components/ui/button';
import { cn } from 'utils/cn';
import type { SortingState, VisibilityState } from '@tanstack/react-table';
import TableTotalsWrapper from '../totals/TableTotalsWrapper';
import TotalsLimitAlert from '../totals/TotalsLimitAlert';

import QuickFilters from './filter-pieces/QuickFilters';
import SearchBar from './filter-pieces/SearchBar';
import { useListFitsContentArea } from './useListFitsContentArea';
import { useScrolledUnder } from './useScrolledUnder';

import DrawerPortal from 'components/drawer/DrawerPortal';
import EmptyState, { emptyStateTitleId } from 'components/EmptyState';
import { plainColumnLabel } from 'components/tables/data-table/columnMeta';
import { DataTable } from 'components/tables/data-table/DataTable';
import { dispatch } from 'store';
import { QueryParams } from 'store/api/common/types';
import { toggleSubDrawer } from 'store/reducers/subdrawer';
import { CountDto } from 'types/count';
import type { ListKey } from 'types/listViews';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter';
import { captureListFiltered, captureListSorted } from 'utils/listAnalytics';
import { ModalArgs } from 'utils/useExcelExport.ts';
import ColumnPickerButton, { ColumnPickerOption } from './columnLayout/ColumnPickerButton';
import { getColumnId, getColumnMeta } from './columnLayout/getColumnId';
import { reconcileColumnOrder } from './columnLayout/reconcileColumnOrder';
import {
    ColumnLayoutStorageContext,
    useColumnLayoutPersistence,
    viewColumnConfigId,
} from './columnLayout/useColumnLayoutPersistence';
import { ViewSearchParamsContext } from './filter-bar/builtInViews';
import FilterBar from './filter-bar/FilterBar';
import QuickFilterChips from './filter-bar/QuickFilterChips';
import { FilterState } from './filter-bar/filterChips';
import { ListHeaderSlotsContext } from './filter-bar/ListHeaderSlots';
import ListViewsScope from './filter-bar/ListViewsScope';
import SaveViewDialog, { ListSnapshot } from './filter-bar/SaveViewDialog';
import { useListViews } from './filter-bar/useListViews';
import ViewsRow from './filter-bar/ViewsRow';
import { useIsNewDesign } from './filter-bar/useIsNewDesign';
import { useRowLink } from './useRowLink';
import FilterPersistenceToggle from './filter-pieces/FilterPersistenceToggle';
import TotalsToggle from './filter-pieces/TotalsToggle';
import { hasUserFilters } from './hasUserFilters';
import MainFilters from './filter-pieces/MainFilters';
import { QuickFilterChangeHandler } from './filter-pieces/quickFilterToggle';
import { convertSortToString, parseSortString } from './sortUtil';
import { ListTableBaseProps } from './ListTableTypes';
import {
    DateRangeFilterForm,
    FilterCheckboxInForm,
    FilterConfig,
    MainFilterForm,
    QuickFilterCheckboxGroupForm,
    QuickFilterDropdownMultiselectForm,
} from './ListTableTypes';
import { useListTableFilter } from './useListTableFilter';

/**
 * `TResponse` lets callers narrow the response shape — endpoints whose envelope is a
 * QueryResponse<DataType> *plus* extra fields (e.g. /v1/expenses/grouped's
 * `totalsByCurrency`) can pass that exact type and avoid `as any` casts.
 */
type Props<
    DataType extends object,
    TResponse extends QueryResponse<DataType> = QueryResponse<DataType>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TQuery default mirrors RTK Query's own `QueryDefinition` generic-default convention (every `@reduxjs/toolkit` exported helper uses `any` for these five slots); tightening to `unknown` here is rejected as invariant by RTK's `UseQuery<QueryDefinition<…>>`. Consumer specifies the concrete shape at the call site.
    TQuery extends QueryDefinition<any, any, any, any, any> = QueryDefinition<any, any, any, any, any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TotalsType default mirrors `ListTableBaseProps`; tightening to `unknown` breaks contravariance for consumer `renderTotals` callbacks.
    TotalsType = any,
> = ListTableBaseProps<DataType, TotalsType> & {
    pluralTextId?: string | Map<string, string>;
    singularTextId?: string | Map<string, string>;
    /** An (i) after the count, explaining what it counts: a message id. */
    countTooltip?: string;
    /**
     * Opt-in: once `total` is known, the count reads as this message, given the rows shown as `shown` and `total`,
     * such as "3 of 5 nominations" for a list that a default filter narrows.
     */
    countOf?: { messageId: string; total: number | undefined };
    /** Hide the "{count} {plural}" counter that sits to the left of quick filters (in the new design beside
     *  its page's title, or with the filter bar's controls where no page header takes it). Use when the page-level row count would be misleading — e.g. My
     *  Portfolio's Groups view where one row collapses multiple owned contacts (and a group can add
     *  a row that holds none). */
    hideElementCount?: boolean;
    /** Title of the empty state shown when the list has no rows. Also the analytics surface label. */
    noEntriesId?: string | Map<string, string>;
    /** One-sentence body under {@link noEntriesId}, saying what will land in this list. */
    noEntriesBodyId?: string;
    /** Empty-state glyph from `components/icons`. Defaults to the generic inbox / no-matches / offline icons. */
    noEntriesIcon?: React.ElementType;
    /**
     * The surface's create action, duplicated in place so the user does not have to find the header
     * button (which moves with permissions, feature flags and breakpoint, and can be scrolled out of
     * an independently scrolling panel). Pass a `PermissionButton` so it is absent when refused.
     * Shown only on the tenant-empty state — never over a filtered or errored list.
     */
    noEntriesAction?: React.ReactNode;
    /**
     * Opt-in: the list query failed. Distinguishes an error from an empty result, which today render
     * identically. Pages pass their query's `isError` alongside `data` / `isLoading`.
     */
    isError?: boolean;
    data?: TResponse;
    filterConfig?: FilterConfig;
    rowsPerPage?: number;
    extraHeaderComponent?: React.ReactNode;
    disableUrlState?: boolean;
    /** The saved-views list this list is, for one shown in a record's tab; a list page's own is read off its path. */
    listKey?: ListKey;
    /** What the list's usage events call it, for a list with no `columnConfigId`, such as a chart report. */
    analyticsSurface?: string;
    initialSearchParams?: string;
    canRememberFilters?: boolean;
    hideClearFilters?: boolean;
    onExportClicked?: () => void;
    /**
     * Optional: Selected row IDs (controlled component pattern).
     * When provided, the table will automatically filter out IDs that are not in the visible data.
     */
    selectedIds?: Set<number>;
    /**
     * Optional: Callback fired when selection changes or when non-visible selections are filtered out.
     * Receives the filtered set of selected IDs.
     */
    onSelectionChange?: (selectedIds: Set<number>) => void;
    /**
     * Paints the rows in `selectedIds` as selected. The memoized table body re-renders on a column
     * change, not on a selection change, so the page's checkbox column has to be rebuilt with the
     * selection for the shading to follow it.
     */
    highlightSelection?: boolean;
    /** Rendered under the quick-filter row (the new design's filter bar), inside the sticky chrome, e.g. a bar
     *  acting on the selected rows. */
    belowQuickFilters?: React.ReactNode;
    fetchData: LazyQueryTrigger<
        QueryDefinition<
            PaginationProps,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            never,
            TResponse,
            'portalAPI'
        >
    >;
    fetchCount?: UseLazyQuery<
        QueryDefinition<
            Pick<PaginationProps, 'filters'>,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            string,
            CountDto,
            'portalAPI'
        >
    >;
    fetchTotals?: UseQuery<TQuery>;
    renderTotals?: (data: TotalsType, isLoading: boolean) => JSX.Element;
    forceTotalsVisible?: boolean;
    /** When true, totals are fetched only after the user clicks "Show totals". Filter/search changes reset that state. */
    onDemandTotals?: boolean;
    /** Initial value for the on-demand "Show totals" toggle. Defaults to false. */
    defaultTotalsRowRequested?: boolean;
    /** Opt-in cap on the number of records the totals query can aggregate, matching the backend's own
     *  cap for that endpoint. Only the endpoints that have one pass it (invoices); an endpoint that
     *  aggregates the whole filtered set server-side leaves it off, or a list larger than the cap
     *  would have its toggle silently forced off with nothing to explain why. */
    totalsLimit?: number;
    /** i18n key for the message shown next to the disabled "Show totals" button when the dataset
     *  exceeds the limit. Goes with `totalsLimit`, whose value it interpolates as `{limit}`. */
    totalsLimitExceededMessageId?: string;
    exportHookResults?: Array<{
        labelKey: string;
        isLoading?: boolean;
        isFetching?: boolean;
        isBusy: boolean;
        /** `surface` names the list in the export's usage events. */
        triggerManualDownload: (
            args: QueryParams,
            elementCount: number,
            modalArgs?: ModalArgs<DataType[]>,
            surface?: string
        ) => Promise<void>;
    }>;
    exportAlertRequireConfirmation?: ModalArgs<DataType[]>;
    renderCustomHeader?: (fetchFilteredData: (searchText: string) => void, searchText: string) => JSX.Element;
    /** Row action buttons, drawn in the quick-filter row after the Clear button; see `QuickFilters` `actions`. */
    renderSearchDescriptionLevelComponent?: () => React.ReactNode;
    /** Optional content rendered in the quick-filter row, in the same slot the quick-filter
     *  inputs occupy (i.e. between the count and the Remember-filters / totals toggles). Use
     *  this for passive legends that should sit where the filter checkboxes would have. The new
     *  design's filter bar has no checkboxes and puts both slots with its controls, at its end. */
    quickFilterLeading?: React.ReactNode;
    /** Optional content rendered as the last cell of the quick-filter row, after the Remember
     *  filters toggle and Clear all filters button. Used for per-page view toggles (e.g.
     *  Groups/Contacts in My Portfolio, List/Grouped in Expenses) that should sit at the
     *  row's right edge rather than next to the search bar. In the new design's filter bar it sits just
     *  before Columns, the row's last control. */
    quickFilterTrailing?: React.ReactNode;
    onFilterChange?: (activeFilter: string) => void;
    onApplyFilterState?: (state: MainFilterForm) => void;
    onTotalCountChange?: (count: number | undefined) => void;
    /** URL params the table should preserve across filter changes and persist (e.g. portfolio scope). */
    preservedParamKeys?: readonly string[];
    /** Rewrites the sort sent to the API, given the user's column sort — for a list that must lead
     *  with a sort key of its own (a grouped list). See `useListTableFilter`. */
    composeSort?: (columnSort: string) => string | string[];
    /**
     * Column affordances, opt-in PER TABLE (all default `false`). A table with a `columnConfigId`
     * does NOT automatically gain these — it must ask. `enableColumnPicker` shows the column
     * picker button; `enableColumnReorder` / `enableColumnResize` enable header drag-reorder /
     * drag-resize (forwarded to the DataTable). Invoices opts into all three; Roles & Permissions
     * into none. All three need a `columnConfigId` to persist into and are ignored without one.
     */
    enableColumnPicker?: boolean;
    enableColumnReorder?: boolean;
    enableColumnResize?: boolean;
    /**
     * Extra class on the DataTable's scroll container. Its main use is to give the container a
     * bounded height (e.g. `max-h-[...]`) so row windowing AND infinite scroll engage: both measure
     * THIS element, so unless it is the bounded scroller (rather than an ancestor) it renders every
     * row and never reaches an end to page from. Pages whose layout doesn't hand the table a bounded
     * height (e.g. Roles & Permissions inside the Settings tabs) cap it here. No-op otherwise.
     */
    dataTableClassName?: string;
};

// TQuery is NOT exposed on this function's signature so the default `QueryDefinition<any, any, any, any, any>` flows verbatim to the inner `useListTableFilter.fetchTotals` slot — exposing TQuery would force tsgo to re-check assignability through the generic and the resulting non-concrete TQuery shape is rejected by RTK's invariant `UseQuery<QueryDefinition<…>>`.
const ListTableBody = <
    DataType extends object,
    TResponse extends QueryResponse<DataType> = QueryResponse<DataType>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TotalsType default mirrors Props alias above; tightening to `unknown` breaks contravariance for consumer renderTotals callbacks.
    TotalsType = any,
>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pinned to the same `any` shape as Props's TQuery default; see Props alias for rationale.
    props: Props<DataType, TResponse, QueryDefinition<any, any, any, any, any>, TotalsType>
) => {
    const {
        renderCustomTable,
        renderCustomHeader,
        columns,
        columnConfigId,
        fetchData,
        fetchCount,
        fetchTotals,
        data,
        filterConfig,
        isLoading,
        extraHeaderComponent,
        exportHookResults,
        exportAlertRequireConfirmation,
        pluralTextId = 'entries',
        singularTextId = 'entry',
        countTooltip,
        countOf,
        hideElementCount = false,
        rowsPerPage = 10,
        disableUrlState = false,
        initialSearchParams,
        canRememberFilters = true,
        hideClearFilters = false,
        noEntriesId = 'no-entries-found',
        noEntriesBodyId,
        noEntriesIcon,
        noEntriesAction,
        isError = false,
        renderSearchDescriptionLevelComponent,
        quickFilterLeading,
        quickFilterTrailing,
        renderTotals,
        forceTotalsVisible,
        onDemandTotals = false,
        defaultTotalsRowRequested = false,
        totalsLimit,
        totalsLimitExceededMessageId,
        onFilterChange,
        onApplyFilterState,
        onTotalCountChange,
        preservedParamKeys,
        composeSort,
        onSortChange: onSortChangeProp,
        selectedIds,
        onSelectionChange,
        highlightSelection = false,
        belowQuickFilters,
        enableColumnPicker = false,
        enableColumnReorder = false,
        enableColumnResize = false,
        dataTableClassName,
        analyticsSurface,
        ...rest
    } = props;

    const isNewDesign = useIsNewDesign();
    const rowLink = useRowLink(rest.getRowHref);
    const viewSearchParams = useContext(ViewSearchParamsContext);
    // A list that owns the URL opens on its view's filters when the page gives it none of its own.
    const listInitialSearchParams = initialSearchParams ?? (disableUrlState ? undefined : viewSearchParams);
    // In the new design a list opens on the user's default view instead of remembered filters.
    const remembersFilters = canRememberFilters && !isNewDesign;

    // Every sort key this table can emit (`convertSortToString` uses the column's sortAccessor,
    // falling back to its id). A sort key outside this set is a leftover from another list, and
    // the filter hook drops it at its fetch boundary — before the API request, the URL and the
    // persisted filter entry — instead of 400ing every fetch.
    const validSortAccessors = useMemo(
        () =>
            (columns ?? []).flatMap((column) => {
                const id = getColumnId(column);
                const { sortAccessor } = getColumnMeta(column);
                return [...(id ? [id] : []), ...(sortAccessor ? [sortAccessor] : [])];
            }),
        [columns]
    );

    const {
        searchBarProps,
        quickFilterProps,
        isInitialFetchDone,
        dataToDisplay,
        handleLoadMore,
        mainFilterProps,
        clearFilters,
        resetFilters,
        handleApplyFilters,
        applyFilterState,
        resolveSelectedOptions,
        filterCount,
        activeFilter,
        isLoadingRealTotalCount,
        realTotalCount,
        totals,
        shouldFetchTotals,
        shouldShowTotals,
        shouldShowTotalsRow,
        isLoadingTotals,
        totalsRowRequested,
        setTotalsRowRequested,
        totalCountForTotals,
        filterPersistenceProps,
        sortState,
        handleSortChange,
        mainFilterCount,
    } = useListTableFilter<DataType, TResponse>({
        fetchData,
        fetchCount,
        fetchTotals,
        forceTotalsVisible,
        onDemandTotals,
        defaultTotalsRowRequested,
        data,
        filterConfig,
        rowsPerPage,
        disableUrlState,
        initialSearchParams: listInitialSearchParams,
        canRememberFilters: remembersFilters,
        onFilterChange,
        onApplyFilterState,
        preservedParamKeys,
        validSortAccessors,
        composeSort,
    });

    // What the list's usage events call it: its own name for them, else the id its columns are kept under, else its
    // empty state's title.
    const surface =
        analyticsSurface ?? columnConfigId ?? (noEntriesId instanceof Map ? noEntriesId.get('default') : noEntriesId);
    const filterState: FilterState = {
        mainFilterState: mainFilterProps.filters,
        quickFilterState: quickFilterProps.quickFilters,
    };
    // One `list_filtered` per filter, clear or search the user commits, with the filters it left applied.
    const trackFiltered = (applied: FilterState | undefined, searchText: string) => {
        if (surface && applied) captureListFiltered({ surface, filterState: applied, searchText });
    };

    // Bubble sort changes up to consumers in addition to the internal refetch driver, so
    // a parent can drive client-side sort (e.g. endpoints whose response shape isn't paginated
    // and ignores the `sort` arg) without losing the URL/refetch behavior.
    const handleSortChangeCombined = useCallback(
        (newSort: string) => {
            handleSortChange(newSort);
            onSortChangeProp?.(newSort);
        },
        [handleSortChange, onSortChangeProp]
    );

    const listViews = useListViews({
        baselineSearch: listInitialSearchParams,
        columnConfigId,
        surface,
        ready: isInitialFetchDone,
        filterConfig,
    });

    // Managed column layout: the toolbar owns the column picker and the hidden-set
    // persistence; widths and stickiness live inside the DataTable. Both read the same
    // entry (useLocalStorage instances sync within the tab), so "Reset columns" here also
    // clears the width overrides the table consumes. An open saved view keeps a working copy
    // of its layout under an entry of its own.
    const layoutConfigId =
        columnConfigId && listViews?.activeView
            ? viewColumnConfigId(columnConfigId, listViews.activeView.id)
            : columnConfigId;
    const columnLayoutPersistence = useColumnLayoutPersistence(layoutConfigId, listViews?.columnStorage);

    const columnPickerOptions = useMemo<ColumnPickerOption[]>(() => {
        if (!columnConfigId || !columns) return [];
        return columns.flatMap((column) => {
            const id = getColumnId(column);
            if (!id) return [];
            const { pickerLabel, lockVisible, fixedPx } = getColumnMeta(column);
            const { header } = column as { header?: unknown };
            return [
                {
                    id,
                    // Locked columns are dropped by the menu itself, so the label only has to hold for
                    // togglable ones.
                    label: plainColumnLabel({ pickerLabel }, header, id),
                    locked: !!lockVisible || fixedPx !== undefined,
                },
            ];
        });
    }, [columnConfigId, columns]);

    const allColumnIds = useMemo(() => columnPickerOptions.map((option) => option.id), [columnPickerOptions]);

    const savedHidden = columnLayoutPersistence.entry?.hidden;
    const savedKnownIds = columnLayoutPersistence.entry?.knownIds;
    const hiddenColumns = useMemo<string[] | undefined>(() => {
        if (!columnConfigId || !columns) return undefined;
        const optionsById = new Map(columnPickerOptions.map((option) => [option.id, option]));
        const defaultHiddenIds = columns.flatMap((column) => {
            const id = getColumnId(column);
            return getColumnMeta(column).defaultHidden && id ? [id] : [];
        });
        // With no saved snapshot, visibility is the column-def defaults. With a snapshot, keep the
        // user's hidden set but also hide any `defaultHidden` column they never saw (absent from
        // `knownIds`) — so columns added to a tab in a later deploy start unchecked even for users
        // who already have a snapshot, while ones they explicitly revealed (in `knownIds`, not in
        // `hidden`) stay visible.
        const base =
            savedHidden === undefined
                ? defaultHiddenIds
                : (() => {
                      const knownIdSet = new Set(savedKnownIds ?? []);
                      const unseenDefaultHidden = defaultHiddenIds.filter((id) => !knownIdSet.has(id));
                      return Array.from(new Set([...savedHidden, ...unseenDefaultHidden]));
                  })();
        // Unknown colIds (column removed/renamed in a later deploy) and locked columns are
        // ignored at read time — entries degrade gracefully without a version field.
        return base.filter((id) => {
            const option = optionsById.get(id);
            return option !== undefined && !option.locked;
        });
    }, [columnConfigId, columns, columnPickerOptions, savedHidden, savedKnownIds]);

    const handleToggleColumn = useCallback(
        (columnId: string) => {
            const current = hiddenColumns ?? [];
            const next = current.includes(columnId) ? current.filter((id) => id !== columnId) : [...current, columnId];
            // Full snapshot, single write per commit (see useColumnLayoutPersistence caveat).
            // Persist the columns known now so a later deploy's new `defaultHidden` columns can be
            // distinguished from ones the user deliberately revealed (see hiddenColumns above).
            columnLayoutPersistence.saveHidden(next, allColumnIds);
        },
        [hiddenColumns, columnLayoutPersistence, allColumnIds]
    );

    // At least one TOGGLABLE column, not just one column: the menu hides locked ones, so a table
    // whose columns are all locked would otherwise open an empty menu.
    const showColumnPicker =
        enableColumnPicker && !!columnConfigId && columnPickerOptions.some((option) => !option.locked);

    // Managed column reorder: the toolbar owns the persisted order too. Derive the
    // controlled `columnOrder` from the saved snapshot run through `reconcileColumnOrder` (drops
    // stale ids, appends new ones, clamps the sticky prefix), mirroring the `hiddenColumns`
    // read-time reconciliation. `allColumnIds` (declared above) follows the column-def order; the
    // sticky prefix is the leading run of `sticky` columns. A header drop persists via `saveOrder`.
    const stickyPrefixIds = useMemo(() => {
        if (!columnConfigId || !columns) return [];
        const ids: string[] = [];
        for (const column of columns) {
            if (!getColumnMeta(column).sticky) break;
            const id = getColumnId(column);
            if (id) ids.push(id);
        }
        return ids;
    }, [columnConfigId, columns]);

    const savedOrder = columnLayoutPersistence.entry?.order;
    const columnOrder = useMemo<string[] | undefined>(() => {
        if (!columnConfigId || !columns) return undefined;
        return reconcileColumnOrder(savedOrder, allColumnIds, stickyPrefixIds);
    }, [columnConfigId, columns, savedOrder, allColumnIds, stickyPrefixIds]);

    // ============================================================================================
    //  DataTable engine bridge. Translates the chrome's sort-string / hidden-id-list contracts into
    //  the DataTable's controlled-state vocabulary.
    // ============================================================================================
    // Column picker: the chrome owns a hidden-id LIST; v8 reads a VisibilityState map keyed by id
    // (false = hidden). Translate the reconciled hidden set into that map for ALL known columns.
    const v8ColumnVisibility = useMemo<VisibilityState | undefined>(() => {
        if (!columnConfigId) return undefined;
        const hiddenSet = new Set(hiddenColumns ?? []);
        const visibility: VisibilityState = {};
        for (const id of allColumnIds) visibility[id] = !hiddenSet.has(id);
        return visibility;
    }, [columnConfigId, hiddenColumns, allColumnIds]);

    // Server-side sort bridge. The chrome owns sort as a STRING ("colId,dir[,nullHandling]"); the
    // table uses SortingState ({ id, desc }[]). `parseSortString` resolves the string to a single
    // rule, matching on column id or sortAccessor.
    const v8Sorting = useMemo<SortingState | undefined>(() => {
        if (!columns) return undefined;
        return (parseSortString(sortState || '', columns) ?? []) as SortingState;
    }, [columns, sortState]);

    // onSortingChange (OnChangeFn<SortingState>) -> the chrome's string-based handler. Resolve the
    // updater against the current sorting, then re-encode via `convertSortToString` (carries the
    // column's sortAccessor + nullHandling) and hand it to `handleSortChangeCombined`, the single
    // channel driving URL state, refetch and the consumer `onSortChange` callback.
    const handleV8SortingChange = useCallback(
        (updater: SortingState | ((old: SortingState) => SortingState)) => {
            if (!columns) return;
            const current = (parseSortString(sortState || '', columns) ?? []) as SortingState;
            const next = typeof updater === 'function' ? updater(current) : updater;
            const sort = convertSortToString(next, columns);
            handleSortChangeCombined(sort);
            if (surface) captureListSorted({ surface, sort });
        },
        [columns, sortState, handleSortChangeCombined, surface]
    );

    // Stable row identity. The engine keys measured row heights and selection by this id, so derive
    // it from a numeric `id` when the row has one, else fall back to the array index.
    const v8GetRowId = useCallback((row: DataType, index: number): string => {
        if (typeof row === 'object' && row !== null && 'id' in row) {
            const id = (row as { id?: unknown }).id;
            if (typeof id === 'number' || typeof id === 'string') return String(id);
        }
        return String(index);
    }, []);

    // Keyed like `v8GetRowId`, which is what the engine matches the selection against.
    const v8RowSelection = useMemo(
        () =>
            highlightSelection && selectedIds
                ? Object.fromEntries(Array.from(selectedIds, (id) => [String(id), true] as const))
                : undefined,
        [highlightSelection, selectedIds]
    );

    const totalsLimitExceededByCount =
        onDemandTotals &&
        totalsLimit !== undefined &&
        totalCountForTotals !== undefined &&
        totalCountForTotals > totalsLimit;

    // Sticky flag: once the BE responds with limitExceeded=true we keep the alert visible
    // even after we force the toggle off (which clears the RTK Query cache via keepUnusedDataFor: 0
    // and would otherwise drop totals.limitExceeded). Set by the response held at mount, cleared on
    // activeFilter change, and set again only by a totals response that arrives after it — the response
    // still held from the previous filter does not set it again. Adjusted during render, when the value
    // it follows changes.
    const [responseLimitExceeded, setResponseLimitExceeded] = useState(
        () => (totals as { limitExceeded?: boolean })?.limitExceeded === true
    );
    const [limitFlagInputs, setLimitFlagInputs] = useState({ activeFilter, totals });
    if (limitFlagInputs.activeFilter !== activeFilter || limitFlagInputs.totals !== totals) {
        setLimitFlagInputs({ activeFilter, totals });
        if (limitFlagInputs.activeFilter !== activeFilter) {
            setResponseLimitExceeded(false);
        }
        if (limitFlagInputs.totals !== totals && (totals as { limitExceeded?: boolean })?.limitExceeded === true) {
            setResponseLimitExceeded(true);
        }
    }

    const effectiveLimitExceeded = !!onDemandTotals && (totalsLimitExceededByCount || responseLimitExceeded);
    const showTotalsLimitAlert = !!totalsLimitExceededMessageId && effectiveLimitExceeded;

    // TODO(react-doctor: no-effect-event-handler): `effectiveLimitExceeded`
    // derives from async totals data — reacting in an effect (not in a handler) is correct.
    // Force the toggle off the moment the limit becomes exceeded.
    useEffect(() => {
        if (effectiveLimitExceeded) {
            setTotalsRowRequested(false);
        }
    }, [effectiveLimitExceeded, setTotalsRowRequested]);

    const totalsToggleProps =
        onDemandTotals && fetchTotals
            ? {
                  enabled: totalsRowRequested,
                  onChange: setTotalsRowRequested,
                  disabled: effectiveLimitExceeded,
              }
            : undefined;

    // **GLOBAL SELECTION FILTERING**: Automatically filter out selections for items that are no longer visible
    // This runs whenever the filtered data changes, ensuring non-visible items are never selected
    useEffect(() => {
        if (!selectedIds || !onSelectionChange || selectedIds.size === 0) {
            return;
        }

        // Get IDs of all visible items (assumes items have an 'id' property)
        const visibleIds = new Set(
            dataToDisplay
                .map((item) => {
                    if (
                        typeof item === 'object' &&
                        item !== null &&
                        'id' in item &&
                        typeof (item as { id?: unknown }).id === 'number'
                    ) {
                        return (item as { id: number }).id;
                    }
                    return undefined;
                })
                .filter((id): id is number => id !== undefined)
        );

        // Filter out selections that are not in the visible data
        const filteredSelection = new Set<number>();
        selectedIds.forEach((id) => {
            if (visibleIds.has(id)) {
                filteredSelection.add(id);
            }
        });

        // Only call onSelectionChange if selections were actually filtered out
        if (filteredSelection.size !== selectedIds.size) {
            onSelectionChange(filteredSelection);
        }
    }, [dataToDisplay, selectedIds, onSelectionChange]);

    const toggleSidebar = useCallback(() => {
        dispatch(toggleSubDrawer('filter-sidebar'));
    }, []);

    const getTextId = useCallback(
        (value: string | Map<string, string>): string | undefined => {
            if (value instanceof Map) {
                const matchingFilter = Object.values(quickFilterProps.quickFilters).find(
                    (el) => el.comperator === value.get('filter')
                );
                let hasFilter = false;
                if (matchingFilter) {
                    if ('type' in matchingFilter && matchingFilter.type === 'dropdown-multiselect') {
                        hasFilter = (matchingFilter as QuickFilterDropdownMultiselectForm).selected?.length > 0;
                    } else {
                        hasFilter = !!(matchingFilter as QuickFilterCheckboxGroupForm).filters?.find(
                            (el: FilterCheckboxInForm) => el.isSelected
                        );
                    }
                }
                return hasFilter ? value.get('hasFilter') : value.get('default');
            } else {
                return value;
            }
        },
        [quickFilterProps]
    );

    const [paginationProps] = useMemo(() => {
        if (!data) return [undefined, undefined];
        const { content, ...rest } = data;
        return [{ ...rest }, content];
    }, [data]);

    const DrawerContent = useMemo(
        () => (
            <MainFilters
                handleChange={mainFilterProps.handleChange}
                filterState={mainFilterProps.filters}
                quickFilters={quickFilterProps.quickFilters}
                handleQuickFilterChange={quickFilterProps.handleChange}
                hideDividers={filterConfig?.hideFilterDividers}
                groupTooltips={filterConfig?.groupTooltips}
            />
        ),
        [mainFilterProps, quickFilterProps, filterConfig?.hideFilterDividers, filterConfig?.groupTooltips]
    );

    const initialLoad = !isInitialFetchDone && !data;
    const hasFilters = filterConfig?.filters.length;
    const exportButtonsCount = exportHookResults?.length ?? 0;
    const exportButtonsLayout = exportButtonsCount > 0 ? 'auto '.repeat(exportButtonsCount) : '';
    const headerLayout = `${hasFilters ? 'auto ' : ''}${remembersFilters ? 'auto ' : ''}${exportButtonsLayout}${showColumnPicker ? 'auto ' : ''}1fr${extraHeaderComponent ? ' auto' : ''}`;

    // Render the (potentially large, expensive) row set at transition priority so React
    // time-slices it and yields to the browser instead of blocking the main thread in one
    // long task. That long task is what starves the header breadcrumb's exit animation
    // — and any other paint — when navigating into a heavy list. The table is
    // already mounted (showing the loader) before rows arrive, so this defers the
    // row-population update rather than the mount; `isRowRenderStale` keeps the loader up
    // during the concurrent render so there's no empty-table flash.
    const deferredDataToDisplay = useDeferredValue(dataToDisplay);
    const isRowRenderStale = deferredDataToDisplay !== dataToDisplay;

    // `rest.isFetching` is folded in as well as `isLoading`: on a REVALIDATION of an already-cached
    // empty combination `tableLoadingProps` reports isFetching (cached args) rather than isLoading,
    // so without it the empty state would render over a request that is still in flight — and flash
    // out again the moment rows land. Kept empty here, the DataTable below draws its skeleton.
    const noData = !dataToDisplay.length && !isLoading && !rest.isFetching;

    const isFiltered = hasUserFilters({
        mainFilterCount,
        mainFilters: mainFilterProps.filters,
        quickFilters: quickFilterProps.quickFilters,
        searchText: searchBarProps.searchTextState,
    });
    // Which of the three no-rows outcomes to show. An error outranks a filter: a failed query says
    // nothing about whether the filters would have matched, so offering "Clear filters" there would
    // send the user chasing a filter that is not the problem.
    const emptyStateVariant = isError ? 'error' : isFiltered ? 'filtered' : 'empty';

    const handleSearchDebounced = useDebouncedCallback((value = '') => {
        searchBarProps.handleSearch(value);
        trackFiltered(filterState, value);
    }, 600);

    // Re-issues the current filter/search/sort combination from page 0 — the same channel a search
    // submit uses, so a retry cannot desynchronise the page index from the rows. Deliberately not
    // memoized: `searchBarProps` is a fresh object every render, so a useCallback would never hold.
    const handleRetry = () => {
        searchBarProps.handleSearch(searchBarProps.searchTextState);
    };

    const elementCount = useMemo(() => {
        if (!isLoadingRealTotalCount && realTotalCount !== undefined) {
            return realTotalCount;
        }
        if (realTotalCount === undefined) {
            return paginationProps?.totalElements ?? 0;
        }
        return 0;
    }, [paginationProps?.totalElements, isLoadingRealTotalCount, realTotalCount]);

    // The new design shows no count it does not have: a list on its first load, or one that failed, has none,
    // and the 0 standing in for it would say the list is empty.
    const isCountKnown =
        !isNewDesign || (!isError && (realTotalCount !== undefined || paginationProps?.totalElements !== undefined));

    useEffect(() => {
        if (isLoadingRealTotalCount) return;
        onTotalCountChange?.(isCountKnown ? elementCount : undefined);
    }, [elementCount, isCountKnown, isLoadingRealTotalCount, onTotalCountChange]);

    // Held in state (not a ref) because the sizing hook has to re-subscribe when the container
    // mounts, and `setState` is a stable callback, so the DataTable's notification effect fires once
    // per container rather than once per render.
    const [tableScrollElement, setTableScrollElement] = useState<HTMLDivElement | null>(null);
    useListFitsContentArea(tableScrollElement);
    const [setChromeArea, isScrolledUnderChrome] = useScrolledUnder();
    const periodQuickFilter = filterConfig?.periodQuickFilter;
    const applyFiltersAndTrack = (next: FilterState) => {
        applyFilterState(next);
        trackFiltered(next, searchBarProps.searchTextState);
    };
    const applyMainFilters = (state: MainFilterForm) =>
        applyFiltersAndTrack({ mainFilterState: state, quickFilterState: quickFilterProps.quickFilters });
    const clearFiltersAndTrack = () => trackFiltered(clearFilters(), '');
    const resetFiltersAndTrack = resetFilters && (() => trackFiltered(resetFilters(), ''));
    const toggleQuickFilterAndTrack: QuickFilterChangeHandler = (value, groupSelector, key, immediateFetch) => {
        const applied = quickFilterProps.handleChange(value, groupSelector, key, immediateFetch);
        // A click passes `immediateFetch` (`toggleQuickFilterOption`), `true` on its one call that fetches. Clearing a
        // disabled group leaves it out, being no action of the user's.
        if (immediateFetch === true) trackFiltered(applied, searchBarProps.searchTextState);
    };
    const quickFilterControls = {
        ...quickFilterProps,
        handleChange: toggleQuickFilterAndTrack,
        handleDropdownChange: (...args: Parameters<typeof quickFilterProps.handleDropdownChange>) =>
            trackFiltered(quickFilterProps.handleDropdownChange(...args), searchBarProps.searchTextState),
        clearFilters: clearFiltersAndTrack,
    };

    const onSubmit = () => {
        return new Promise<void>((resolve, reject) => {
            try {
                for (const [, mainFilter] of Object.entries(mainFilterProps.filters)) {
                    if (
                        mainFilter &&
                        (mainFilter.type === 'daterange' || mainFilter.type === 'offset-date-time-range')
                    ) {
                        const checkedMainFilter = mainFilter as DateRangeFilterForm;
                        const { from, to, label, labelTo } = checkedMainFilter;
                        validateDate(from, label || 'from date');
                        validateDate(to, labelTo || label || 'to date');
                    }
                }
                handleApplyFilters();
                trackFiltered(filterState, searchBarProps.searchTextState);
                resolve();
            } catch (error) {
                console.error('Error in filter submission', error);
                reject(error);
            }
        });
    };

    function validateDate(date: string | null, fieldLabel: string) {
        if (date) {
            const parsedDate = new Date(date);
            if (isNaN(parsedDate.getTime())) {
                errorAlert(t('validation.invalid-filter', { label: fieldLabel }));
                throw new Error('Invalid date format');
            }
        }
    }

    const searchBar = (className?: string) => (
        <SearchBar
            className={className}
            value={searchBarProps.searchTextState}
            onChange={(event) => {
                const value = event.target.value;
                searchBarProps.handleSearchBarChange(value);
                handleSearchDebounced(value);
            }}
            singularTextId={getTextId(singularTextId)}
            pluralTextId={getTextId(pluralTextId)}
            count={`${elementCount}${isLoadingRealTotalCount ? '+' : ''}`}
            // The new design's search box is the plain field with its placeholder; the syntax still applies.
            showSearchTips={!isNewDesign}
        />
    );

    const exportButtons = exportHookResults?.map((exportHookResult) => (
        <Button
            key={exportHookResult.labelKey}
            {...buttonStyle(
                { variant: 'outlined' },
                isNewDesign ? 'border-border-control bg-paper text-foreground h-9 px-3.5 shadow-none' : undefined
            )}
            // Nothing to export produces an empty file, so the button is dead while
            // the list is empty rather than silently downloading a header-only sheet.
            disabled={exportHookResult.isBusy || noData}
            onClick={() => {
                exportHookResult.triggerManualDownload(
                    { filter: activeFilter },
                    elementCount,
                    exportAlertRequireConfirmation,
                    surface
                );
            }}
        >
            {isNewDesign ? null : (
                <span className={ICON_SLOT.medium}>
                    <FileDownloadOutlined />
                </span>
            )}
            <FormattedMessage id={exportHookResult.labelKey} />
        </Button>
    ));

    const columnPicker = showColumnPicker ? (
        <ColumnPickerButton
            options={columnPickerOptions}
            hidden={hiddenColumns ?? []}
            onToggle={handleToggleColumn}
            onReset={columnLayoutPersistence.reset}
        />
    ) : null;

    const shownCount = (
        <span className="text-body1 font-semibold">
            {elementCount}
            {isLoadingRealTotalCount ? '+' : null}
        </span>
    );
    const elementCountLabel =
        hideElementCount || !isCountKnown ? null : (
            // min-width fits a 7-digit count plus the loading "+" suffix, so the
            // quick filters to the right don't shift as the count loads/changes.
            // self-start + h-8 (the height of a checkbox filter row; the dropdown
            // variant is also 32px)
            // keeps the count centered against the FIRST row of quick filters
            // when they wrap to multiple lines, instead of floating between them.
            // The new design draws it among the filter row's trailing controls,
            // centred on them and at the row's gap: what follows it is pinned to
            // the row's end, so its width shifts nothing there.
            <div className="new:min-w-0 new:self-center flex h-8 min-w-36 items-center self-start max-sm:items-end">
                {countOf?.total === undefined ? (
                    <>
                        {shownCount}
                        &nbsp;
                        <div className="whitespace-nowrap lowercase">
                            <FormattedPlural
                                value={elementCount}
                                one={<FormattedMessage id={getTextId(singularTextId)} />}
                                other={<FormattedMessage id={getTextId(pluralTextId)} />}
                            />
                        </div>
                    </>
                ) : (
                    <div className="whitespace-nowrap">
                        <FormattedMessage
                            id={countOf.messageId}
                            values={{
                                shown: shownCount,
                                total: countOf.total,
                            }}
                        />
                    </div>
                )}
                {countTooltip ? (
                    <InfoTooltip text={countTooltip} label={t(getTextId(pluralTextId) ?? '')} className="ms-1 h-4" />
                ) : null}
            </div>
        );

    // The new design's title row takes the count and Export where the page's header offers a place for them; a
    // list that does not own the URL is not the page's list (a modal's, a grouped list's parts).
    const headerSlots = useContext(ListHeaderSlotsContext);
    const ownsHeader = isNewDesign && !disableUrlState;
    const countSlot = ownsHeader && !hideElementCount ? headerSlots?.count : null;
    const actionsSlot = ownsHeader ? headerSlots?.actions : null;

    const sorted = v8Sorting?.[0];
    const sortedColumn = sorted ? columns?.find((column) => getColumnId(column) === sorted.id) : undefined;
    const sortedHeader = (sortedColumn as { header?: unknown } | undefined)?.header;
    const listSnapshot: ListSnapshot = {
        filterState,
        sort:
            sorted && typeof sortedHeader === 'string' ? { column: sortedHeader, descending: sorted.desc } : undefined,
        shownColumns: showColumnPicker
            ? columnPickerOptions.filter((option) => !option.locked && !hiddenColumns?.includes(option.id)).length
            : undefined,
    };

    const filterPersistenceToggle = remembersFilters ? (
        <FilterPersistenceToggle enabled={filterPersistenceProps.enabled} onChange={filterPersistenceProps.onChange} />
    ) : null;

    // What a load ended in, for assistive tech: the empty state's title, or how many rows there are, and nothing
    // where the count is hidden, not known or still being counted. It keeps what it said while a request runs, so only a load
    // that ends in something else is said: not the same count again after each page of rows or each refetch.
    const pluralId = getTextId(pluralTextId);
    const countNounId = elementCount === 1 ? getTextId(singularTextId) : pluralId;
    let settledOutcome = '';
    if (noData) {
        settledOutcome = t(emptyStateTitleId(emptyStateVariant, getTextId(noEntriesId)));
    } else if (!hideElementCount && isCountKnown && !isLoadingRealTotalCount && countNounId) {
        settledOutcome =
            countOf?.total === undefined
                ? `${elementCount} ${t(countNounId)}`
                : t(countOf.messageId, { shown: String(elementCount), total: String(countOf.total) });
    }
    const [outcome, setOutcome] = useState('');
    if (!initialLoad && !isLoading && !rest.isFetching && outcome !== settledOutcome) {
        setOutcome(settledOutcome);
    }

    return (
        <>
            {isNewDesign ? null : (
                <DrawerPortal
                    content={DrawerContent}
                    titleId="all-filters"
                    drawerId="filter-sidebar"
                    onFooterSecondaryAction={hideClearFilters ? undefined : clearFiltersAndTrack}
                    onFooterPrimaryAction={onSubmit}
                    applyColor="secondary"
                    width={filterConfig?.mainFilterWidth ?? 744}
                    type="sub"
                    afterTitle={filterCount ? <FilterCountBadge count={filterCount} /> : null}
                    beforeFooterActions={filterPersistenceToggle}
                />
            )}
            {/* Only the rows scroll: the table is sized to the room under this chrome (see
                useListFitsContentArea). Sticky for the pages where the list cannot be sized and the
                page scrolls instead; a page that keeps a strip of its own stuck at its top sets
                `--list-chrome-top` to `auto`, and this scrolls away under it. */}
            <div
                ref={setChromeArea}
                className={cn(
                    'bg-paper new:bg-background sticky top-[var(--list-chrome-top,0px)] z-30 transition-shadow',
                    isScrolledUnderChrome && 'shadow-sticky-top'
                )}
            >
                <div className="flex flex-col gap-4 pt-2">
                    {renderCustomHeader
                        ? renderCustomHeader(searchBarProps.handleSearch, searchBarProps.searchTextState)
                        : null}
                    {listViews ? <ViewsRow model={listViews} /> : null}
                    {isNewDesign ? (
                        <FilterBar
                            search={searchBar('border-border-control h-9 w-60 shadow-none')}
                            state={filterState}
                            onApply={applyFiltersAndTrack}
                            onResolveOptions={resolveSelectedOptions}
                            onReset={listViews?.isChanged ? listViews.reset : undefined}
                            // A list with saved views goes back through Reset alone; Clear all is for one without.
                            onClearAll={listViews || hideClearFilters || !isFiltered ? undefined : clearFiltersAndTrack}
                            groupTooltips={filterConfig?.groupTooltips}
                            quickFilters={
                                Object.keys(quickFilterProps.quickFilters).length || periodQuickFilter ? (
                                    <QuickFilterChips
                                        {...quickFilterControls}
                                        mainFilterState={mainFilterProps.filters}
                                        handleMainFilterStateChange={mainFilterProps.handleChange}
                                        period={periodQuickFilter}
                                        applyMainFilters={applyMainFilters}
                                    />
                                ) : undefined
                            }
                            shownByQuickFilters={periodQuickFilter ? [periodQuickFilter.selector] : undefined}
                            // The trailing controls (Group by) sit just before Columns, always the row's last.
                            actions={
                                <>
                                    {quickFilterLeading}
                                    {renderSearchDescriptionLevelComponent?.()}
                                    {totalsToggleProps ? <TotalsToggle {...totalsToggleProps} /> : null}
                                    {filterPersistenceToggle}
                                    {countSlot
                                        ? isCountKnown &&
                                          createPortal(
                                              <>
                                                  <FormattedNumber value={elementCount} />
                                                  {isLoadingRealTotalCount ? '+' : null}
                                              </>,
                                              countSlot
                                          )
                                        : elementCountLabel}
                                    {actionsSlot ? createPortal(exportButtons, actionsSlot) : exportButtons}
                                    {extraHeaderComponent}
                                    {quickFilterTrailing}
                                    {columnPicker ? (
                                        // The picker's trigger as the row's ghost text button.
                                        <span className="[&>button]:text-foreground-secondary [&>button]:text-meta contents [&>button]:h-8 [&>button]:border-0 [&>button]:bg-transparent [&>button]:px-2.5 [&>button]:font-medium [&>button]:shadow-none [&>button>span]:hidden">
                                            {columnPicker}
                                        </span>
                                    ) : null}
                                </>
                            }
                        />
                    ) : (
                        <>
                            {/* Dynamic slot template (filter/export/columns buttons) stays inline — it is
                            computed at runtime from which slots are present. */}
                            <div className="grid gap-2" style={{ gridTemplateColumns: headerLayout }}>
                                {hasFilters ? (
                                    <Button {...buttonStyle({ variant: 'outlined' })} onClick={toggleSidebar}>
                                        <span className={ICON_SLOT.medium}>
                                            <FilterList />
                                        </span>
                                        <FormattedMessage id="filter" />
                                        {filterCount ? <FilterCountBadge count={filterCount} /> : null}
                                    </Button>
                                ) : null}
                                {filterPersistenceToggle}
                                {exportButtons}
                                {columnPicker}
                                {searchBar()}
                                {extraHeaderComponent ? extraHeaderComponent : null}
                            </div>

                            <div className="flex gap-2">
                                <div
                                    className={cn(
                                        'flex w-full items-center max-sm:flex-col max-sm:items-start',
                                        Object.keys(quickFilterProps.quickFilters).length === 0 && 'mb-2'
                                    )}
                                >
                                    {elementCountLabel}
                                    <QuickFilters
                                        {...quickFilterControls}
                                        mainFilterState={mainFilterProps.filters}
                                        handleMainFilterStateChange={mainFilterProps.handleChange}
                                        totalsToggleProps={totalsToggleProps}
                                        leading={quickFilterLeading}
                                        hideLeadingDivider={hideElementCount}
                                        trailing={quickFilterTrailing}
                                        actions={renderSearchDescriptionLevelComponent?.()}
                                        hideClearFilters={hideClearFilters}
                                        reset={
                                            resetFiltersAndTrack && periodQuickFilter
                                                ? { onReset: resetFiltersAndTrack, period: periodQuickFilter }
                                                : undefined
                                        }
                                        chips={filterConfig?.quickFilterChips}
                                        period={periodQuickFilter}
                                        applyMainFilters={applyMainFilters}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {belowQuickFilters}
                    {showTotalsLimitAlert ? (
                        <TableTotalsWrapper isLoading={false}>
                            <TotalsLimitAlert>
                                <FormattedMessage id={totalsLimitExceededMessageId!} values={{ limit: totalsLimit }} />
                            </TotalsLimitAlert>
                        </TableTotalsWrapper>
                    ) : renderTotals && shouldShowTotals ? (
                        renderTotals(totals, isLoadingTotals)
                    ) : renderTotals && fetchTotals && shouldFetchTotals ? (
                        // Totals are on their way — reserve the slot at its final height so the
                        // table doesn't get pushed down when they arrive.
                        <TableTotalsWrapper isLoading />
                    ) : null}
                </div>
            </div>
            {renderCustomTable
                ? renderCustomTable(
                      dataToDisplay,
                      initialLoad,
                      noData,
                      getTextId(noEntriesId),
                      paginationProps,
                      handleLoadMore,
                      { variant: emptyStateVariant, onClearFilters: clearFiltersAndTrack, onRetry: handleRetry }
                  )
                : null}
            {noData && !initialLoad && !renderCustomTable ? (
                // `!initialLoad` keeps the state off the first render pass, before the mount effect
                // has issued the first fetch — `isLoading` is still false there, so without it an
                // empty state would flash ahead of the loading skeleton on every navigation.
                //
                // The filter chrome above stays MOUNTED: the filter that emptied the list has to be
                // undoable, so the empty state replaces the table only, never the page.
                <EmptyState
                    size="page"
                    variant={emptyStateVariant}
                    icon={noEntriesIcon}
                    titleId={getTextId(noEntriesId)}
                    bodyId={noEntriesBodyId}
                    action={noEntriesAction}
                    onClearFilters={clearFiltersAndTrack}
                    onRetry={handleRetry}
                    searchText={isNewDesign ? searchBarProps.searchTextState : undefined}
                    surface={getTextId(noEntriesId)}
                />
            ) : null}
            {!noData && !renderCustomTable ? (
                // The DataTable renders through this chrome, which forwards the identical
                // deferred data (the chrome's single useDeferredValue — `deferredDataToDisplay`),
                // staleness-folded loader (`isFetching = rest.isFetching || isRowRenderStale`),
                // managed persistence (columnConfigId / visibility / order), totals, server sort and
                // scroll restoration. Paging is the engine's: it fires `onLoadMore` when the user
                // scrolls to the end of the rows in its own scroll container.
                <ColumnLayoutStorageContext.Provider value={listViews?.columnStorage}>
                    <DataTable<DataType, TotalsType>
                        data={deferredDataToDisplay}
                        columns={columns ?? []}
                        getRowId={v8GetRowId}
                        columnConfigId={layoutConfigId}
                        enableColumnReorder={enableColumnReorder}
                        enableColumnResize={enableColumnResize}
                        className={dataTableClassName}
                        columnVisibility={v8ColumnVisibility}
                        columnOrder={columnOrder}
                        onColumnOrderChange={columnLayoutPersistence.saveOrder}
                        sorting={v8Sorting}
                        onSortingChange={handleV8SortingChange}
                        rowSelection={v8RowSelection}
                        onRowClick={rest.onRowClick}
                        getRowHref={rowLink.getRowHref}
                        onRowNavigate={rowLink.onRowNavigate}
                        isRowEnabled={rest.isRowEnabled}
                        rowEdgeColor={rest.rowEdgeColor}
                        expandedRowEdgeColor={rest.expandedRowEdgeColor}
                        renderExpandedContent={rest.renderExpandedContent}
                        renderRowLeadIn={rest.renderRowLeadIn}
                        renderFullRow={rest.renderFullRow}
                        renderStickyLeadIn={rest.renderStickyLeadIn}
                        totals={totals}
                        showTotalsRow={shouldShowTotalsRow && !effectiveLimitExceeded}
                        isLoadingTotals={isLoadingTotals}
                        animateTotalsRow={isNewDesign}
                        isLoading={isLoading}
                        isFetching={rest.isFetching || isRowRenderStale}
                        scrollRestorationId={rest.scrollRestorationId}
                        onLoadMore={handleLoadMore}
                        hasNextPage={!!paginationProps && !paginationProps.last}
                        // Also latched on the deferred-row staleness: while the rows React has
                        // committed lag behind the response, the sentinel sits at the end of the OLD
                        // row set, so its position says nothing about whether another page is needed.
                        isLoadingMore={isLoading || rest.isFetching || isRowRenderStale}
                        onScrollElementChange={setTableScrollElement}
                        ariaLabel={pluralId ? t(pluralId) : undefined}
                    />
                </ColumnLayoutStorageContext.Provider>
            ) : null}
            {listViews ? <SaveViewDialog model={listViews} snapshot={listSnapshot} /> : null}
            {/* Mounted before its text: a live region that arrives with its words is not reliably read. */}
            <div role="status" className="sr-only">
                {outcome}
            </div>
        </>
    );
};

/**
 * A list with its filters. In the new design a list that owns its URL on a list page, or in a record's tab that
 * names its `listKey`, also gets that list's saved views; on the list page it opens on the user's default one.
 */
const ListTableWithFilters = <
    DataType extends object,
    TResponse extends QueryResponse<DataType> = QueryResponse<DataType>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TotalsType default mirrors Props alias above; tightening to `unknown` breaks contravariance for consumer renderTotals callbacks.
    TotalsType = any,
>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pinned to the same `any` shape as Props's TQuery default; see Props alias for rationale.
    props: Props<DataType, TResponse, QueryDefinition<any, any, any, any, any>, TotalsType>
) => {
    const isNewDesign = useIsNewDesign();
    const list = <ListTableBody<DataType, TResponse, TotalsType> {...props} />;
    return isNewDesign && !props.disableUrlState ? (
        <ListViewsScope listKey={props.listKey}>{list}</ListViewsScope>
    ) : (
        list
    );
};

export default ListTableWithFilters;

/** The number of applied drawer filters, as an orange disc beside the filter button and the drawer's title. */
function FilterCountBadge({ count }: { count: number }) {
    return (
        <span className="bg-warning text-grey-0 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-xs leading-5 font-medium">
            {count}
        </span>
    );
}
